import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Trash2 } from "lucide-react";
import { readJson } from "@/hooks/admin/adminApi";
import { SubBadge } from "@/components/admin/SectionShell";

const REASONS = { ok: "Success", not_in_batch: "Mobile not in batch", wrong_password: "Wrong password", other_device: "Other device active", classroom_off: "Classroom off", invalid_mobile: "Invalid mobile", no_batch: "No batch running", not_started: "Classroom not started", error: "Error" };
const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

const Stat = ({ label, value, tone = "", testId }) => (
  <div data-testid={testId} className="rounded-lg border border-border bg-white px-3 py-2 min-w-[7rem]">
    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className={`font-serif text-2xl leading-tight ${tone}`}>{value}</p>
  </div>
);

export const LoginStatsCard = ({ authFetch }) => {
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => authFetch("/admin/classroom/login-stats").then(readJson).then(setStats).catch(() => {});
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const clear = async () => {
    if (!window.confirm("Are you sure you want to clear all classroom login statistics?")) return;
    const res = await authFetch("/admin/classroom/login-stats", { method: "DELETE" });
    if (res.ok) { toast.success("Login statistics cleared"); load(); } else toast.error("Failed to clear");
  };
  if (!stats) return null;
  return (
    <div data-testid="login-stats-card" className="rounded-xl border border-border bg-white/60 p-4 mb-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-medium flex items-center gap-2"><SubBadge /> "Open Classroom" attempts</p>
          <p className="text-[11px] text-muted-foreground">Every login try from the website link or a personal classroom link (success and failure, with reason).</p>
        </div>
        <div className="flex gap-2">
          <button type="button" data-testid="login-stats-refresh" onClick={load} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-xs hover:bg-muted"><RefreshCw size={12} /> Refresh</button>
          <button type="button" data-testid="login-stats-clear" onClick={clear} className="inline-flex items-center gap-1.5 rounded-full border border-red-200 text-red-600 px-3 py-1.5 text-xs hover:bg-red-50"><Trash2 size={12} /> Clear</button>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Stat testId="login-stats-total" label="Total attempts" value={stats.total} />
        <Stat testId="login-stats-success" label="Success" value={stats.success} tone="text-emerald-700" />
        <Stat testId="login-stats-failed" label="Failed" value={stats.failed} tone="text-red-700" />
        <Stat testId="login-stats-24h" label="Last 24h (ok / fail)" value={`${stats.last24h.success} / ${stats.last24h.failed}`} />
        <Stat testId="login-stats-unique" label="Participants logged in" value={stats.unique_success_mobiles} />
      </div>
      {stats.failed > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="login-stats-reasons">
          {Object.entries(stats.reasons).sort((a, b) => b[1] - a[1]).map(([r, n]) => <span key={r} className="rounded-md border border-red-200 bg-red-50 text-red-800 px-2 py-0.5 text-[11px]"><b>{n}</b> × {REASONS[r] || r}</span>)}
        </div>
      )}
      <button type="button" data-testid="login-stats-toggle" onClick={() => setOpen((o) => !o)} className="mt-3 text-xs underline text-muted-foreground hover:text-foreground">{open ? "Hide" : "Show"} recent attempts ({stats.recent.length})</button>
      {open && (
        <div className="mt-2 overflow-auto max-h-[280px]" data-testid="login-stats-table">
          <table className="w-full text-xs">
            <thead className="text-left uppercase tracking-widest text-muted-foreground"><tr>{["Time (IST)", "Result", "Mobile", "Name entered", "Participant", "Via", "Device"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
            <tbody>
              {stats.recent.map((r) => (
                <tr key={r.id} className="border-t border-border/60">
                  <td className="px-2 py-1.5 whitespace-nowrap">{fmt(r.ts)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap"><span className={`rounded-md px-1.5 py-0.5 ${r.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`}>{REASONS[r.reason] || r.reason}</span></td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{r.mobile || "—"}</td>
                  <td className="px-2 py-1.5">{r.name_entered || "—"}</td>
                  <td className="px-2 py-1.5">{r.participant || "—"}</td>
                  <td className="px-2 py-1.5">{r.via}</td>
                  <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[14rem]" title={r.device}>{r.device || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
